import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { SaleDetails_SaleProduct } from 'app/models/dms/saleDetails.model';
import { SaleProductsSearchParamsModel } from 'app/models/dms/saleProductsSearchParams.model';
import { SaleService } from 'app/services/sale.service';
import { UserService } from 'app/services/user.service';
import { ProductClass } from 'app/utility/enums';

@Component({
    selector: 'app-select-product',
    templateUrl: './select-product.component.html',
    styleUrls: ['./select-product.component.scss'],
    standalone: false
})
export class SelectProductComponent implements OnInit
{
    @Input() products: SaleDetails_SaleProduct[];
    @Input() selectedProduct: SaleDetails_SaleProduct;
    @Input() title: string;
    @Input() placeholderText: string;
    @Input() productClass: ProductClass = ProductClass.General;
    @Input() saleId: string;
    @Input() vehicleId: string;
    @Input() multiSelect: boolean = false;

    searchText: string = '';
    public filteredProducts: SaleDetails_SaleProduct[] = [];
    public selectedProducts: SaleDetails_SaleProduct[] = [];
    private selectedProductKeys: Set<string> = new Set<string>();

    sortOrder: string = 'code-asc';
    sortOptions = [
        { value: 'code-asc', label: 'Product Code' },
        { value: 'code-desc', label: 'Product Code (desc)' },
        { value: 'description-asc', label: 'Description' },
        { value: 'description-desc', label: 'Description (desc)' },
        { value: 'salePrice-asc', label: 'Sale Price' },
        { value: 'salePrice-desc', label: 'Sale Price (desc)' }
    ];

    constructor(
        public modalController: ModalController,
        public saleService: SaleService,
        public userService: UserService,
    )
    {

    }

    ngOnInit()
    {
        this.products = this.products || [];
        this.filteredProducts = this.products;

        if (this.multiSelect && this.selectedProduct)
        {
            this.toggleProductSelection(this.selectedProduct, true);
        }

        this.sortProducts();
    }

    onKeypress(event: KeyboardEvent)
    {
        if (event.key === "Enter")
        {
            this.filterProducts();
        }
    }

    onSearchInput(event: any)
    {
        this.searchText = event.target.value;

        this.filterProducts();

    }

    filterProducts()
    {
        this.filteredProducts = this.products.filter(product =>
            product.caption.toLowerCase().includes(this.searchText.toLowerCase())
        );
    }

    dismissModal(product: SaleDetails_SaleProduct = null)
    {
        if (product == null)
        {
            this.modalController.dismiss({
                'result': {
                    save: false
                }
            });
            return;
        }
        else if (product != null)
        {
            this.modalController.dismiss({
                'result': {
                    save: true,
                    product: product
                }
            });
        }
    }

    onProductClick(product: SaleDetails_SaleProduct)
    {
        if (!this.multiSelect)
        {
            this.dismissModal(product);
            return;
        }

        this.toggleProductSelection(product);
    }

    isSelected(product: SaleDetails_SaleProduct): boolean
    {
        return this.selectedProductKeys.has(this.getProductKey(product));
    }

    saveSelectedProducts()
    {
        if (!this.multiSelect)
        {
            return;
        }

        this.modalController.dismiss({
            'result': {
                save: true,
                products: this.selectedProducts
            }
        });
    }

    scrollTop: boolean = true;
    ionScrollEvent(ev: any)
    {
        var scrollTop = (ev.detail.scrollTop == 0);
        if (this.scrollTop != scrollTop)
        {
            this.scrollTop = scrollTop;
        }
    }

    sortProducts()
    {

        switch (this.sortOrder)
        {
            case 'code-asc':
                this.filteredProducts.sort((a, b) => a.caption.localeCompare(b.caption));
                break;
            case 'code-desc':
                this.filteredProducts.sort((a, b) => b.caption.localeCompare(a.caption));
                break;
            case 'description-asc':
                this.filteredProducts.sort((a, b) => a.description.localeCompare(b.description));
                break;
            case 'description-desc':
                this.filteredProducts.sort((a, b) => b.description.localeCompare(a.description));
                break;
            case 'salePrice-asc':
                this.filteredProducts.sort((a, b) => a.saleAmount - b.saleAmount);
                break;
            case 'salePrice-desc':
                this.filteredProducts.sort((a, b) => b.saleAmount - a.saleAmount);
        }

    }

    private getProductKey(product: SaleDetails_SaleProduct): string
    {
        return product?.productId || product?.saleProductId || product?.caption || '';
    }

    private toggleProductSelection(product: SaleDetails_SaleProduct, forceSelect: boolean = false)
    {
        const key = this.getProductKey(product);
        if (!key)
        {
            return;
        }

        const isCurrentlySelected = this.selectedProductKeys.has(key);
        const shouldSelect = forceSelect || !isCurrentlySelected;

        if (shouldSelect)
        {
            if (!isCurrentlySelected)
            {
                this.selectedProductKeys.add(key);
                this.selectedProducts.push(product);
            }
            return;
        }

        this.selectedProductKeys.delete(key);
        this.selectedProducts = this.selectedProducts.filter(x => this.getProductKey(x) !== key);
    }

    doRefresh(event)
    {

        this.fetchObject();

        setTimeout(() =>
        {
            event.target.complete();
        }, 2000);
    }


    async fetchObject()
    {
        if (this.userService.mcMinVersion("3.2.23") && this.saleId && this.vehicleId)
        {
            var searchParams = new SaleProductsSearchParamsModel();
            searchParams.productClass = this.productClass;
            searchParams.searchText = this.searchText;
            searchParams.saleId = this.saleId;
            searchParams.vehicleId = this.vehicleId;

            this.saleService.getSaleProducts(searchParams)
                .subscribe({
                    next: x =>
                    {
                        this.products = x.products;
                        this.filteredProducts = this.products;
                        this.sortProducts();
                        this.filterProducts();
                    },
                    error: err =>
                    {
                        this.userService.handleError(err);
                    }
                });

        }
        else
        {
            await this.saleService.GetProductsByProductClass(this.productClass)
                .subscribe({
                    next: x =>
                    {
                        this.products = x;
                        this.filteredProducts = this.products;
                        this.sortProducts();
                        this.filterProducts();
                    },
                    error: err =>
                    {
                        this.userService.handleError(err);
                    }
                });
        }

    }
}
